import React from "react";
import glamorous from "glamorous";
import moment from "moment";
import _ from "lodash";
import { Table } from "react-bootstrap";
import { Tabs, TabList, Tab, TabPanel } from "react-tabs";
import "../../styles/App.css";
import "react-tabs/style/react-tabs.css";

import Colors from "../../styles/colors";
import Error from "react-icons/lib/md/error-outline";
import { getArrivalWindowText } from "./ArrivalWindow";

const tabStyle = {
  display: "flex",
  flex: 1,
  justifyContent: "space-around",
  alignItems: "center",
  padding: "0.5em",
  color: Colors.background.GRAY_BLUE,
  marginRight: "0.5em"
};

const tabPanelStyle = {
  border: "1px solid #aaa",
  borderRadius: "3px"
};

const DetailsTable = props => {
  const headers = props.headers.map((h, i) => <th key={i}>{h}</th>);

  return (
    <glamorous.Div
      css={{
        display: "block",
        overflowY: "auto",
        backgroundColor: "white",
        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.05)",
        borderRadius: "3px"
      }}
    >
      <Table condensed striped>
        <glamorous.Thead css={{ color: Colors.background.GRAY_BLUE }}>
          <tr>{headers}</tr>
        </glamorous.Thead>
        <tbody>{props.rows}</tbody>
      </Table>
    </glamorous.Div>
  );
};

// Updates tab
const UpdatesTable = props => {
  const { exceptions, statuses } = props;

  const statusRows = statuses
    ? statuses.map((s, i) => {
        const statusTime = moment.utc(s.actual_created_at).local();

        return {
          time: statusTime,
          rowObject: (
            <glamorous.Tr
              key={`status ${i}`}
              css={{ color: Colors.background.GRAY_BLUE }}
            >
              <glamorous.Td css={{ fontWeight: "bold" }}>
                {s.status_name}
              </glamorous.Td>
              <glamorous.Td css={{ fontStyle: "italic" }}>
                {s.status_type_name}
              </glamorous.Td>
              <td>
                <glamorous.Span css={{ fontWeight: "bold" }}>
                  {statusTime.format("HH:mm")}
                </glamorous.Span>
                <br />
                <span>{statusTime.format("l")}</span>
              </td>
            </glamorous.Tr>
          )
        };
      })
    : [];

  const exceptionRows = exceptions
    ? exceptions.map((e, i) => {
        const exceptionTime = moment.utc(e.created_utc).local();

        return {
          time: exceptionTime,
          rowObject: (
            <glamorous.Tr key={`exception ${i}`}>
              <glamorous.Td
                css={{
                  color: Colors.highlight.RED,
                  fontWeight: "bold",
                  minWidth: "10em"
                }}
              >
                <Error
                  size={"1.5em"}
                  style={{ marginRight: "0.25em", marginTop: "-2px" }}
                />
                {e.name}
              </glamorous.Td>
              <glamorous.Td css={{ minWidth: "10em" }}>{""}</glamorous.Td>
              <glamorous.Td css={{ minWidth: "10em" }}>
                <glamorous.Span css={{ fontWeight: "bold" }}>
                  {exceptionTime.format("HH:mm")}
                </glamorous.Span>
                <br />
                <span>{exceptionTime.format("l")}</span>
              </glamorous.Td>
            </glamorous.Tr>
          )
        };
      })
    : [];

  const sortedRows = [...statusRows, ...exceptionRows]
    .sort((a, b) => (a.time < b.time ? 1 : -1))
    .map(r => r.rowObject);

  return (
    <DetailsTable headers={["Status", "Type", "Time"]} rows={sortedRows} />
  );
};

const getStopOrder = (stops, index) => {
  if (index === 0) {
    return "O";
  } else if (index === stops.length - 1) {
    return "D";
  } else {
    return index.toString();
  }
};

const buildAddressFromStop = stop => {
  return (
    stop.location.address +
    ", " +
    stop.location.city +
    ", " +
    stop.location.state
  );
};

// Stops Tab
const StopsTable = props => {
  const { mode, references, stops } = props;

  if (!stops) {
    return null;
  }

  // There are certain instances where we miss a stop
  // in the sequence, and arrive at a later stop
  // in those cases, we do not want to show ETA for any
  // stop before a stop we have arrived at
  let foundActual = false;
  let showETA = {};
  for (var i = stops.length - 1; i >= 0; i--) {
    if (stops[i].arrived_at != null) {
      foundActual = true;
    }
    showETA[i] = !foundActual;
  }

  let rows = stops.map((s, i) => {
    const hasArrivalTime = s.arrived_at != null;
    const hasETA = s.eta != null;
    const { windowTimeText, windowDateText } = getArrivalWindowText(s);

    return (
      <glamorous.Tr key={i}>
        <glamorous.Td css={{ fontWeight: "bold" }}>
          {getStopOrder(stops, i)}
        </glamorous.Td>
        <glamorous.Td>
          <glamorous.Span css={{ fontWeight: "bold" }}>
            {s.location.code}
          </glamorous.Span>
          <br />
          <span>{s.location.name}</span>
          <br />
          <span style={{ fontSize: "small" }}>{buildAddressFromStop(s)}</span>
        </glamorous.Td>
        <glamorous.Td minWidth="10em">
          <glamorous.Div fontWeight="bold">{windowTimeText}</glamorous.Div>
          <div>{windowDateText}</div>
        </glamorous.Td>
        {(showETA[i] === false || !hasETA) && <td />}
        {showETA[i] === true &&
          hasETA && (
            <glamorous.Td css={{ minWidth: "10em" }}>
              <glamorous.Span css={{ fontStyle: "italic" }}>
                {moment
                  .utc(s.eta)
                  .local()
                  .format("HH:mm")}
              </glamorous.Span>
              <br />
              <span>
                {moment
                  .utc(s.eta)
                  .local()
                  .format("l")}
              </span>
            </glamorous.Td>
          )}
        {hasArrivalTime === false && <td>N/A</td>}
        {hasArrivalTime === true && (
          <glamorous.Td css={{ minWidth: "10em" }}>
            <glamorous.Span css={{ fontWeight: "bold" }}>
              {moment
                .utc(s.arrived_at)
                .local()
                .format("HH:mm")}
            </glamorous.Span>
            <br />
            <span>
              {moment
                .utc(s.arrived_at)
                .local()
                .format("l")}
            </span>
          </glamorous.Td>
        )}
      </glamorous.Tr>
    );
  });

  let interchangeRows =
    mode === "Rail"
      ? references
          .filter(r => _.includes(["ETAI"], r.qualifier))
          .sort((r1, r2) => {
            const etai1 = _.split(r1.value, "|");
            const eta1 = moment(etai1[3], "YYYY-MM-DD-HH:mm:SSS");
            const etai2 = _.split(r2.value, "|");
            const eta2 = moment(etai2[3], "YYYY-MM-DD-HH:mm:SSS");

            return eta1.valueOf() - eta2.valueOf();
          })
          .map((r, i) => {
            const etai = _.split(r.value, "|");

            let loc = `${etai[0]}, ${etai[1]}`;
            let code = etai[2];
            let eta = etai[3];
            let tz = etai[4];

            return (
              <glamorous.Tr key={`rail${i}`}>
                <glamorous.Td css={{ fontWeight: "bold" }}>I</glamorous.Td>
                <glamorous.Td>
                  <glamorous.Span css={{ fontWeight: "bold" }}>
                    {loc}
                  </glamorous.Span>
                  <br />
                  <span>{code}</span>
                </glamorous.Td>
                <glamorous.Td minWidth="10em">
                  <glamorous.Span css={{ fontWeight: "bold" }}>
                    {moment(eta, "YYYY-MM-DD-HH:mm:SSS")
                      .utcOffset(tz)
                      .local()
                      .format("HH:mm")}
                  </glamorous.Span>
                  <br />
                  <span>
                    {moment(eta, "YYYY-MM-DD-HH:mm:SSS")
                      .utcOffset(tz)
                      .local()
                      .format("l")}
                  </span>
                </glamorous.Td>
                <glamorous.Td minWidth="10em">
                  <glamorous.Span css={{ fontStyle: "italic" }}>
                    {moment(eta, "YYYY-MM-DD-HH:mm:SSS")
                      .utcOffset(tz)
                      .local()
                      .format("HH:mm")}
                  </glamorous.Span>
                  <br />
                  <span>
                    {moment(eta, "YYYY-MM-DD-HH:mm:SSS")
                      .utcOffset(tz)
                      .local()
                      .format("l")}
                  </span>
                </glamorous.Td>
                <glamorous.Td css={{ minWidth: "10em" }}>N/A</glamorous.Td>
              </glamorous.Tr>
            );
          })
      : null;

  let allRows =
    mode === "Rail" && interchangeRows
      ? [...rows.slice(0, 1), ...interchangeRows, ...rows.slice(1, rows.length)]
      : rows;

  return (
    <DetailsTable
      headers={["", "Stop", "Scheduled", "Estimated", "Actual"]}
      rows={allRows}
    />
  );
};

// Coordinates Tab
const CoordinatesTable = props => {
  const { coords } = props;

  // Sort the time in descending order, so most
  // recent update is at top of list
  const sortedCoords = _.orderBy(coords, ["time"], ["desc"]);

  const rows = sortedCoords.map((c, i) => {
    return (
      <glamorous.Tr key={i}>
        <glamorous.Td>
          <glamorous.Span css={{ fontWeight: "bold" }}>
            {moment
              .utc(c.time)
              .local()
              .format("l HH:mm")}
          </glamorous.Span>
        </glamorous.Td>
        <td>{c.latitude}</td>
        <td>{c.longitude}</td>
      </glamorous.Tr>
    );
  });

  return (
    <DetailsTable headers={["Time", "Latitude", "Longitude"]} rows={rows} />
  );
};

const PartsTable = props => {
  const { parts } = props;

  // Combine ASN parts and part values into one row for each unique part name
  let combinedParts = {};
  parts.forEach(p => {
    const partType = p.qualifier;
    const partName = p.name.split(" ")[0];
    if (partName in combinedParts) {
      combinedParts[partName][partType] = p.quantity;
    } else {
      combinedParts[partName] = { [partType]: p.quantity };
    }
  });

  const rows = Object.keys(combinedParts).map((p, i) => {
    const partValues = combinedParts[p];
    return (
      <glamorous.Tr key={i}>
        <td>{p}</td>
        <td>{partValues.asnpart}</td>
        <td>{partValues.part}</td>
      </glamorous.Tr>
    );
  });

  return <DetailsTable headers={["Part", "Planned", "Actual"]} rows={rows} />;
};

const ReferencesTable = props => {
  const { mode, references } = props;

  const railReferences = [
    "equip_initial",
    "equipment_number",
    "ITEM",
    "BM",
    "equipment_description_code",
    "routing_sequence",
    "waybill_sender",
    "XVIN"
  ];

  const referencesToRemove = ["part", "asnpart", "SCAC", "CARRIER"];

  const rows = references
    .filter(
      r =>
        mode === "Truck"
          ? !_.includes(referencesToRemove, r.qualifier)
          : _.includes(railReferences, r.qualifier)
    )
    .sort((r1, r2) => {
      let qualifier1 = r1.qualifier.toLowerCase();
      let qualifier2 = r2.qualifier.toLowerCase();
      let createdAt1 = r1.created_at.valueOf();
      let createdAt2 = r2.created_at.valueOf();

      if (qualifier1 < qualifier2) {
        return -1;
      }
      if (qualifier1 > qualifier2) {
        return 1;
      }

      if (createdAt1 < createdAt2) {
        return -1;
      }
      if (createdAt1 > createdAt2) {
        return 1;
      }

      return 0;
    })
    .map((r, i) => {
      return (
        <glamorous.Tr key={i}>
          <td>{r.qualifier}</td>
          <td>{r.value}</td>
        </glamorous.Tr>
      );
    });

  return <DetailsTable headers={["Qualifier", "Value"]} rows={rows} />;
};

const hasParts = shipment =>
  shipment.part_numbers != null && shipment.part_numbers.length > 0;
const hasRefs = shipment =>
  shipment.shipment_references != null &&
  shipment.shipment_references.length > 0;

const ShipmentDetailTabs = props => {
  const { shipment } = props;

  return (
    <Tabs style={{ margin: "1em", marginTop: "3em", flex: 1 }}>
      <TabList
        style={{
          display: "flex",
          flexDirection: "row",
          marginBottom: 0,
          border: "none"
        }}
      >
        <Tab style={tabStyle}>Updates</Tab>
        <Tab style={tabStyle}>Stops</Tab>
        <Tab style={tabStyle}>Coordinates</Tab>
        {hasParts(shipment) && <Tab style={tabStyle}>Parts</Tab>}
        {hasRefs(shipment) && (
          <Tab style={{ ...tabStyle, marginRight: 0 }}>VINs & References</Tab>
        )}
      </TabList>

      <TabPanel style={tabPanelStyle}>
        <UpdatesTable
          exceptions={shipment.shipment_exceptions}
          statuses={shipment.shipment_statuses}
        />
      </TabPanel>

      <TabPanel style={tabPanelStyle}>
        <StopsTable
          stops={shipment.shipment_stops}
          mode={shipment.mode_name}
          references={shipment.shipment_references}
        />
      </TabPanel>

      <TabPanel style={tabPanelStyle}>
        <CoordinatesTable
          coords={
            shipment.current_location ? shipment.current_location.updates : []
          }
        />
      </TabPanel>

      {hasParts(shipment) && (
        <TabPanel style={tabPanelStyle}>
          <PartsTable parts={shipment.part_numbers} />
        </TabPanel>
      )}
      {hasRefs(shipment) && (
        <TabPanel style={tabPanelStyle}>
          <ReferencesTable
            references={shipment.shipment_references}
            mode={shipment.mode_name}
          />
        </TabPanel>
      )}
    </Tabs>
  );
};

export default ShipmentDetailTabs;



// WEBPACK FOOTER //
// ./src/modules/shipment-detail/ShipmentDetailTabs.js